import { useState } from "react";
import { MessageCircle } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { api } from "@/lib/api";
import { todayIso } from "@/lib/tracks";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { fillText, smsHref, myFirstName } from "@/lib/textExamples";

// Opens the phone's messages app with a chosen example pre-filled, and logs it as a connection.
export default function TextGuestButton({ guest, orgId, logGuestId, examples = [], churchName = "", onLogged, testid }) {
  const tid = testid || `guest-row-text-${guest.id}`;
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const hasPhone = !!(guest.phone || "").replace(/\D/g, "");
  const stop = (e) => e.stopPropagation();

  const send = async (ex) => {
    const body = fillText(ex.text, { Name: guest.first_name, "Your Name": myFirstName(user), Church: churchName });
    setOpen(false);
    const gid = logGuestId === undefined ? guest.id : logGuestId;
    if (gid) {
      try {
        await api.post(`/coord/guests/${gid}/connections`, { kind: "other", custom_label: `Text: ${ex.title}`, date: todayIso(), notes: "" }, { params: { org_id: orgId } });
        onLogged?.();
      } catch { /* sending the text matters more than the log */ }
    }
    window.location.href = smsHref(guest.phone, body);
  };

  const label = hasPhone ? `Text ${guest.first_name || "guest"}` : "No phone on file";
  return (
    <div className="shrink-0" onClick={stop}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild disabled={!hasPhone}>
          <button
            disabled={!hasPhone}
            className="h-9 px-3 rounded-full flex items-center gap-1.5 text-xs transition-colors enabled:hover:bg-[var(--surface-hover)] disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ border: "1px solid var(--border-strong)", color: "var(--ink-2)" }}
            title={label}
            aria-label={label}
            data-testid={tid}
          >
            <MessageCircle size={14} /> <span className="hidden sm:inline">{hasPhone ? "Text" : "No phone"}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" collisionPadding={12} onClick={stop} className="w-72 p-0 py-1 max-h-80 overflow-y-auto" style={{ background: "var(--bg-deep)", border: "1px solid var(--border-strong)" }} data-testid={`${tid}-menu`}>
          <div className="px-3 py-1.5 text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>Text {guest.first_name || "guest"}</div>
          {examples.length === 0 && <div className="px-3 py-2 text-xs" style={{ color: "var(--muted)" }}>No text examples yet.</div>}
          {examples.map((ex, i) => (
            <button key={ex.id || i} onClick={() => send(ex)} className="w-full text-left px-3 py-2 text-sm hover:bg-[var(--surface-hover)]" style={{ color: "var(--ink)" }} data-testid={`${tid}-pick-${i}`}>
              {ex.title}
            </button>
          ))}
        </PopoverContent>
      </Popover>
    </div>
  );
}
